import { useEffect, useState } from 'react';
import { Trophy, Gem } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useProgress } from '@/hooks/useProgress';

interface LeaderboardEntry {
  rank: number;
  is_current_user: boolean;
}

/**
 * Compact two-tile strip showing Leaderboard rank + Gems on the Profile tab.
 * These stats used to live on the Home weekly recap card and were moved here
 * to reduce Home cognitive load.
 */
export function ProfileRankAndGems() {
  const { user } = useAuth();
  const { progress } = useProgress();
  const [rank, setRank] = useState<number | null>(null);
  const [loadingRank, setLoadingRank] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoadingRank(false);
      return;
    }
    let cancelled = false;
    supabase
      .rpc('get_leaderboard', { result_limit: 200 })
      .then(({ data }) => {
        if (cancelled) return;
        if (data) {
          const me = (data as LeaderboardEntry[]).find((r) => r.is_current_user);
          if (me) setRank(me.rank);
        }
        setLoadingRank(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="flex items-center gap-2 rounded-xl bg-muted/40 border border-border px-3 py-2">
        <div className="p-1.5 rounded-md bg-accent/15 flex-shrink-0">
          <Trophy className="w-4 h-4 text-accent" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-body text-muted-foreground leading-tight">
            Leaderboard
          </p>
          <p className="display-number text-sm text-foreground truncate">
            {loadingRank ? '…' : rank ? `#${rank}` : '—'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 rounded-xl bg-muted/40 border border-border px-3 py-2">
        <div className="p-1.5 rounded-md bg-purple-500/15 flex-shrink-0">
          <Gem className="w-4 h-4 text-purple-400" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-body text-muted-foreground leading-tight">
            Gems
          </p>
          <p className="display-number text-sm text-foreground truncate">
            {progress.gems.toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
}
